import React, { useState } from "react";
import Icon from "@mdi/react";
import {
	mdiInfinity,
	mdiAccountBox,
	mdiInvoiceListOutline,
	mdiAccountWrench,
	mdiMessageText,
	mdiToolbox,
	mdiFileTree,
	mdiListStatus,
	mdiMessageTextOutline,
	mdiToolboxOutline,
	mdiListBoxOutline,
	mdiListBox,
	mdiCalendarClock,
	mdiCalendarClockOutline,
	mdiBell,
	mdiBookOpenPageVariant,
	mdiBookOpenPageVariantOutline,
	mdiAccountTie,
	mdiAccountTieOutline,
} from "@mdi/js";
import styles from "./styles.module.scss";
import { useAccount, useNotifications } from "shared/hooks";
import { navigate } from "shared/utils";
import { clsx } from "@ismael1361/utils";
import { Link, NotificationsPanel, UserAvatar } from "components";
import { AccountModel } from "shared/model";

interface NavItem {
	icon: string;
	label: string;
	href: string;
	disabled?: boolean;
	isActive?: (user?: AccountModel | null) => boolean;
	isHidden?: (user?: AccountModel | null) => boolean;
}

const createNavItem = (
	icon: string | ((isActive: boolean) => string),
	label: string,
	href: string,
	options?: Omit<NavItem, "icon" | "label" | "href">,
): NavItem => {
	return {
		get icon() {
			if (typeof icon === "function") {
				return icon(this.isActive?.() || false);
			}
			return icon;
		},
		label,
		href,
		...options,
	};
};

const topNavItems: NavItem[] = [
	createNavItem(
		(isActive) => {
			return isActive ? mdiMessageText : mdiMessageTextOutline;
		},
		"Meu assistente",
		"/",
		{
			isActive() {
				return window.location.pathname === "/" || /^\/team\//i.test(window.location.pathname);
			},
		},
	),
	// {
	// 	icon: mdiAccountGroup,
	// 	label: "Minhas Equipes",
	// 	href: "/workspaces",
	// 	isActive: () => /^\/workspaces/i.test(window.location.pathname),
	// },
	// {
	// 	icon: mdiAccountBox,
	// 	label: "Meus Membros",
	// 	href: "/my-teams",
	// 	isActive: () => window.location.pathname === "/my-teams",
	// },
	// {
	// 	icon: mdiTextBoxMultipleOutline,
	// 	label: "Biblioteca",
	// 	href: "/library",
	// 	isActive: () => window.location.pathname === "/library",
	// },
	createNavItem(
		(isActive) => {
			return isActive ? mdiToolbox : mdiToolboxOutline;
		},
		"Ferramentas",
		"/tools",
		{
			isActive: () => /^\/tools/i.test(window.location.pathname),
		},
	),
	createNavItem(
		(isActive) => {
			return isActive ? mdiAccountTie : mdiAccountTieOutline;
		},
		"Especialistas",
		"/specialists",
		{
			isActive: () => /^\/specialists/i.test(window.location.pathname),
		},
	),
	createNavItem(
		(isActive) => {
			return isActive ? mdiListBox : mdiListBoxOutline;
		},
		"Tarefas",
		"/tasks",
		{
			isActive: () => /^\/tasks(?!-agendadas)/i.test(window.location.pathname) && !/^\/scheduled-tasks/i.test(window.location.pathname),
		},
	),
	createNavItem(
		(isActive) => {
			return isActive ? mdiCalendarClock : mdiCalendarClockOutline;
		},
		"Agendamentos",
		"/scheduled-tasks",
		{
			isActive: () => /^\/scheduled-tasks/i.test(window.location.pathname),
		},
	),
	createNavItem(
		(isActive) => {
			return isActive ? mdiBookOpenPageVariant : mdiBookOpenPageVariantOutline;
		},
		"Base de Conhecimento",
		"/knowledge-base",
		{
			isActive: () => /^\/knowledge-base/i.test(window.location.pathname),
		},
	),
];

const bottomNavItems: NavItem[] = [
	createNavItem(mdiInvoiceListOutline, "Faturamento", "/", { disabled: true }),
	// createNavItem(mdiCog, "Preferências", "/settings", { isActive: () => /^\/settings/i.test(window.location.pathname) }),
	createNavItem(mdiAccountWrench, "Backoffice", "/backoffice", {
		isHidden: (user) => !user?.containsPermissionBy(2),
		isActive: () => /^\/backoffice/i.test(window.location.pathname),
	}),
];

export const IconSidebar: React.FC = () => {
	const user = useAccount();
	const { unreadCount } = useNotifications();
	const [notifAnchor, setNotifAnchor] = useState<HTMLElement | null>(null);

	return (
		<aside className={styles.sidebar}>
			<div className={styles.topGroup}>
				<Link
					href="/"
					className={styles.logo}
				>
					<Icon
						path={mdiInfinity}
						size={0.8}
						style={{ color: "white", fontWeight: 800 }}
					/>
				</Link>
				<nav className={styles.nav}>
					{topNavItems.map((item, i) => {
						if (item.isHidden?.(user)) return null;
						const isActive = item.isActive?.(user);
						return (
							<Link
								key={i}
								href={item.href}
								className={clsx(styles.navItem, isActive && styles.active)}
								disabled={item.disabled}
								title={item.label}
							>
								<Icon
									path={item.icon}
									size={1}
								/>
								<span className={styles.navLabel}>{item.label}</span>
							</Link>
						);
					})}
				</nav>
			</div>
			<div className={styles.bottomGroup}>
				<nav className={styles.nav}>
					<button
						type="button"
						aria-label="Notificações"
						title="Notificações"
						className={clsx(styles.navItem, styles.bellBtn)}
						onClick={(e) => setNotifAnchor(e.currentTarget)}
					>
						<Icon path={mdiBell} size={1} />
						{unreadCount > 0 && (
							<span className={styles.bellBadge}>{unreadCount > 99 ? "99+" : unreadCount}</span>
						)}
						<span className={styles.navLabel}>Avisos</span>
					</button>
					<NotificationsPanel
						anchorEl={notifAnchor}
						onClose={() => setNotifAnchor(null)}
					/>
					{bottomNavItems.map((item, i) => {
						if (item.isHidden?.(user)) return null;
						const isActive = item.isActive?.(user);
						return (
							<Link
								key={i}
								href={item.href}
								className={clsx(styles.navItem, isActive && styles.active)}
								disabled={item.disabled}
								title={item.label}
							>
								<Icon
									path={item.icon}
									size={1}
								/>
								<span className={styles.navLabel}>{item.label}</span>
							</Link>
						);
					})}
				</nav>
				<button
					type="button"
					className={styles.avatar}
					onClick={() => navigate("/settings")}
					title="Meu Perfil"
				>
					<UserAvatar user={user} size={32} interactive />
					<span className={styles.navLabel}>Perfil</span>
				</button>
			</div>
		</aside>
	);
};
